/**
 * WizardShell
 *
 * Full-screen overlay: position fixed; inset 0; z-index 50.
 * Layout:
 *   LEFT SIDEBAR (~280px) — logo, step indicators, resume button
 *   MAIN CONTENT — top bar, scrollable content, fixed bottom action bar
 */

import React, { useState, useCallback } from 'react';
import { Check, RotateCcw, PlayCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ProjectsLogo } from '@/components/ProjectsLogo';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useAuth } from '@/providers/AuthProvider';
import { WizardModeProvider, useWizardMode } from '@/contexts/WizardModeContext';
import { WIZARD_STEPS, TOTAL_STEPS, WELCOME_STEP, COMPLETION_STEP } from './wizardSteps';
import { WelcomeScreen } from './WelcomeScreen';
import { CompletionScreen } from './CompletionScreen';
import { Step1CompanySetup } from './steps/Step1CompanySetup';
import { Step2FinanceReporting } from './steps/Step2FinanceReporting';
import { Step2bTargets } from './steps/Step2bTargets';
import { Step3ProjectConfig } from './steps/Step3ProjectConfig';
import { Step4BudgetSettings } from './steps/Step4BudgetSettings';
import { Step7Thresholds } from './steps/Step7Thresholds';
import { Step8BillingSetup } from './steps/Step8BillingSetup';
import { Step9ProjectFields } from './steps/Step9ProjectFields';
import { Step10KeyPeople } from './steps/Step10KeyPeople';
import { Step5UserPermissions } from './steps/Step5UserPermissions';
import { Step6Templates } from './steps/Step6Templates';
import { Step6bEmailTemplates } from './steps/Step6bEmailTemplates';
import { Step7Notifications } from './steps/Step7Notifications';
import { Step8Integrations } from './steps/Step8Integrations';

// Per-step skip tracking — records which steps were skipped
export type StepStatus = 'pending' | 'completed' | 'skipped';

const INITIAL_STEP_STATUSES: Record<number, StepStatus> = Object.fromEntries(
  WIZARD_STEPS.map((s) => [s.id, 'pending']),
);

// Outer shell wraps the inner with WizardModeProvider so embedded settings
// pages can auto-register their save functions and hide their own save bars.
export function WizardShell() {
  return (
    <WizardModeProvider>
      <WizardShellInner />
    </WizardModeProvider>
  );
}

function WizardShellInner() {
  const { authenticatedUser, isProduction } = useAuth();
  const { triggerSave } = useWizardMode();
  const {
    savedStep,
    saveStep,
    dismissOnboarding,
    completeOnboarding,
    resetOnboarding,
  } = useOnboarding();

  const [currentStep, setCurrentStep] = useState<number>(savedStep);
  const [stepStatuses, setStepStatuses] = useState<Record<number, StepStatus>>(
    INITIAL_STEP_STATUSES,
  );

  const firstName = authenticatedUser?.name?.split(' ')[0] ?? 'there';

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
      saveStep(step);
    },
    [saveStep],
  );

  const markStepCompleted = useCallback((stepId: number) => {
    setStepStatuses((prev) => ({ ...prev, [stepId]: 'completed' }));
  }, []);

  const markStepSkipped = useCallback((stepId: number) => {
    setStepStatuses((prev) => ({ ...prev, [stepId]: 'skipped' }));
  }, []);

  const handleNext = useCallback(
    async (stepId: number) => {
      // Trigger save on any embedded settings page before advancing.
      // We intentionally catch save errors so that a backend failure never
      // blocks the user from progressing through the onboarding wizard.
      try {
        await triggerSave();
      } catch (err) {
        console.warn('[WizardShell] Save error during onboarding step — continuing anyway:', err);
      }
      markStepCompleted(stepId);
      const next = stepId + 1;
      if (next > TOTAL_STEPS) {
        goToStep(COMPLETION_STEP);
      } else {
        goToStep(next);
      }
    },
    [triggerSave, markStepCompleted, goToStep],
  );

  const handleSkip = useCallback(
    (stepId: number) => {
      markStepSkipped(stepId);
      const next = stepId + 1;
      if (next > TOTAL_STEPS) {
        goToStep(COMPLETION_STEP);
      } else {
        goToStep(next);
      }
    },
    [markStepSkipped, goToStep],
  );

  const handleBack = useCallback(
    (stepId: number) => {
      goToStep(stepId - 1);
    },
    [goToStep],
  );

  const handleStart = useCallback(() => {
    goToStep(1);
  }, [goToStep]);

  const handleSkipOnboarding = useCallback(() => {
    dismissOnboarding();
  }, [dismissOnboarding]);

  const handleFinish = useCallback(() => {
    completeOnboarding(stepStatuses);
  }, [completeOnboarding, stepStatuses]);

  const handleReset = useCallback(() => {
    setCurrentStep(WELCOME_STEP);
    setStepStatuses(INITIAL_STEP_STATUSES);
    resetOnboarding();
  }, [resetOnboarding]);

  // ── Step renderer ─────────────────────────────────────────────────────────
  const renderStep = () => {
    if (currentStep === WELCOME_STEP) {
      return (
        <WelcomeScreen
          firstName={firstName}
          onStart={handleStart}
          onSkip={handleSkipOnboarding}
        />
      );
    }
    if (currentStep === COMPLETION_STEP) {
      return (
        <CompletionScreen
          stepStatuses={stepStatuses}
          onFinish={handleFinish}
        />
      );
    }

    const commonProps = {
      onNext: () => handleNext(currentStep),
      onBack: () => handleBack(currentStep),
      onSkip: () => handleSkip(currentStep),
      isLastStep: currentStep === TOTAL_STEPS,
    };

    switch (currentStep) {
      // ── Getting Started ────────────────────────────────────────────────
      case 1:  return <Step8Integrations {...commonProps} />;
      case 2:  return <Step1CompanySetup {...commonProps} />;
      // ── Finance & Reporting ────────────────────────────────────────────
      case 3:  return <Step2FinanceReporting {...commonProps} />;
      case 4:  return <Step2bTargets {...commonProps} />;
      // ── Project Configuration ──────────────────────────────────────────
      case 5:  return <Step3ProjectConfig {...commonProps} />;
      case 6:  return <Step4BudgetSettings {...commonProps} />;
      case 7:  return <Step7Thresholds {...commonProps} />;
      case 8:  return <Step8BillingSetup {...commonProps} />;
      case 9:  return <Step9ProjectFields {...commonProps} />;
      case 10: return <Step10KeyPeople {...commonProps} />;
      // ── Users & Access ─────────────────────────────────────────────────
      case 11: return <Step5UserPermissions {...commonProps} />;
      // ── Templates & Comms ──────────────────────────────────────────────
      case 12: return <Step6Templates {...commonProps} />;
      case 13: return <Step6bEmailTemplates {...commonProps} />;
      case 14: return <Step7Notifications {...commonProps} />;
      default: return null;
    }
  };

  const isOnWelcome = currentStep === WELCOME_STEP;
  const isOnCompletion = currentStep === COMPLETION_STEP;
  const isOnStep = !isOnWelcome && !isOnCompletion;

  // ── Top bar text ──────────────────────────────────────────────────────────
  const topBarTitle = isOnWelcome
    ? 'Welcome to Projects'
    : isOnCompletion
      ? 'Setup Complete'
      : `Step ${currentStep} of ${TOTAL_STEPS} — ${WIZARD_STEPS[currentStep - 1]?.name ?? ''}`;

  return (
    <div className="fixed inset-0 z-50 flex bg-background">
      {/* ── LEFT SIDEBAR ─────────────────────────────────────────────────── */}
      <aside className="w-[280px] flex-shrink-0 flex flex-col border-r border-border bg-primary/5">
        {/* Logo + App name */}
        <div className="px-6 py-5 flex items-center gap-3 border-b border-border">
          <ProjectsLogo className="h-8 w-8 flex-shrink-0" />
          <span className="font-bold text-base text-foreground">Projects</span>
        </div>

        {/* Setup label */}
        <div className="px-6 pt-5 pb-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            Onboarding Setup
          </p>
        </div>

        {/* Step indicators — grouped by stage */}
        <nav className="flex-1 px-4 overflow-y-auto pb-4">
          {(() => {
            const rendered: React.ReactNode[] = [];
            let lastStage = '';

            WIZARD_STEPS.forEach((step) => {
              const status = stepStatuses[step.id] ?? 'pending';
              const isActive = currentStep === step.id;
              const isCompleted = status === 'completed';

              // Emit a stage header whenever the stage changes
              if (step.stage !== lastStage) {
                lastStage = step.stage;
                rendered.push(
                  <p
                    key={`stage-${step.stage}`}
                    className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/60 px-3 pt-4 pb-1"
                  >
                    {step.stage}
                  </p>,
                );
              }

              rendered.push(
                <div
                  key={step.id}
                  className={cn(
                    'flex items-start gap-3 rounded-lg px-3 py-2',
                    isActive && 'bg-primary/10',
                  )}
                >
                  {/* Circle badge */}
                  <div
                    className={cn(
                      'mt-0.5 h-5 w-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all',
                      isCompleted
                        ? 'bg-primary text-primary-foreground'
                        : isActive
                          ? 'bg-primary/15 ring-2 ring-primary text-primary'
                          : 'bg-muted text-muted-foreground',
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      step.id
                    )}
                  </div>

                  {/* Step info */}
                  <div className="min-w-0">
                    <p
                      className={cn(
                        'text-xs font-semibold leading-tight',
                        isActive ? 'text-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {step.name}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
                      {step.sidebarDescription}
                    </p>
                  </div>
                </div>,
              );
            });

            return rendered;
          })()}
        </nav>

        {/* Sidebar footer — Reset (dev/non-production only) */}
        {!isProduction && (
          <div className="border-t border-border p-4 space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start gap-2 text-xs"
              onClick={handleReset}
              title="Dev: clear all onboarding state and restart"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset (Dev)
            </Button>
          </div>
        )}
      </aside>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-8 border-b border-border bg-background">
          <span className="text-sm font-medium text-muted-foreground">{topBarTitle}</span>

          <div className="flex items-center gap-3">
            {/* Skip Onboarding — hidden on Completion screen */}
            {!isOnCompletion && (
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground text-xs"
                onClick={handleSkipOnboarding}
              >
                ✕ Skip Onboarding
              </Button>
            )}
          </div>
        </div>

        {/* Scrollable content — overflow-y-auto lets WelcomeScreen / CompletionScreen scroll;
            StepWrapper uses h-full so its content never exceeds this container. */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {renderStep()}
        </div>
      </div>
    </div>
  );
}
